import { useEffect, useRef, useState } from "react";
import DarkLightMode from "@/components/UI/DarkLightMode";
import SizeSelector from "@/components/UI/SizeSelector";
import InfoBanner from "@/components/UI/InfoBanner";
import useTableUIStore from "@/store/tableUIStore";
import useReleaseNotesStore from "@/store/releaseNotesStore";
import useModalStore from "@/store/modalStore";
import { getConfig } from "../../../config";
import { fetchRequestWrapper } from "@/lib/utilities";
import { useAuth0 } from "@auth0/auth0-react";
import { useTheme } from "next-themes";
import { sanitizeReleaseNoteHtml } from "./GeneralIndexHelper";
import { returnStringFormattedDateTime } from "@/lib/globalFormatterHelper";

const getAPIVersion = async (
  getAccessTokenSilently: Function,
  setAPIVersion: any,
) => {
  const returnAPIVersion = await fetchRequestWrapper(
    {
      url: `/version/product`,
      method: "GET",
    },
    getAccessTokenSilently,
  );

  if (returnAPIVersion) {
    setAPIVersion(returnAPIVersion);
  }
};

const GeneralIndex = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { theme } = useTheme();
  const [apiVersion, setApiVersion] = useState("");

  useEffect(() => {
    getAPIVersion(getAccessTokenSilently, setApiVersion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -- RELEASE NOTES
  const releaseNote = useReleaseNotesStore((state) => state.releaseNote);
  const markAsSeen = useReleaseNotesStore((state) => state.markAsSeen);
  const scrollToReleaseNotes = useModalStore(
    (state) => state.scrollToReleaseNotes,
  );
  const clearScrollToReleaseNotes = useModalStore(
    (state) => state.clearScrollToReleaseNotes,
  );
  const releaseNotesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (releaseNote) {
      markAsSeen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [releaseNote]);

  useEffect(() => {
    if (scrollToReleaseNotes && releaseNotesRef.current) {
      releaseNotesRef.current.scrollIntoView({ behavior: "smooth" });
      clearScrollToReleaseNotes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToReleaseNotes]);

  // -- FONT SIZE
  const fontSize = useTableUIStore((state) => state.fontSize);
  const setFontSize = useTableUIStore((state) => state.setFontSize);
  // -- ROW HEIGHT
  const rowHeight = useTableUIStore((state) => state.rowHeight);
  const setRowHeight = useTableUIStore((state) => state.setRowHeight);

  const fontSizeOptions = [
    { value: "small", label: "Small" },
    { value: "medium", label: "Medium" },
    { value: "large", label: "Large" },
    // { value: "x-large", label: "X-Large" }, Removed X-Large as an option for the moment.
  ];

  const rowHeightOptions = [
    { value: "narrow", label: "Narrow" },
    { value: "standard", label: "Standard" },
    { value: "expanded", label: "Expanded" },
  ];
  return (
    <div className="flex w-full flex-col space-y-8">
      {/* Theme Settings */}
      <div className="space-y-1">
        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="h-4 w-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4.098 19.902a3.75 3.75 0 005.304 0l6.401-6.402M6.75 21A3.75 3.75 0 013 17.25V4.125C3 3.504 3.504 3 4.125 3h5.25c.621 0 1.125.504 1.125 1.125v4.072M6.75 21a3.75 3.75 0 003.75-3.75V8.197M6.75 21h13.125c.621 0 1.125-.504 1.125-1.125v-5.25c0-.621-.504-1.125-1.125-1.125h-4.072M10.5 8.197l2.88-2.88c.438-.439 1.15-.439 1.59 0l3.712 3.713c.44.44.44 1.152 0 1.59l-2.879 2.88M6.75 17.25h.008v.008H6.75v-.008z"
            />
          </svg>
          Theme
        </h3>

        <div className="dark:hover:bg-gray-750 ml-8 flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 hover:bg-gray-100 max-[1000px]:flex-col max-[1000px]:items-start dark:border-gray-700 dark:bg-gray-800">
          <div className="flex flex-col">
            <div className="text-base font-medium">
              Appearance - {theme === "dark" ? "Dark" : "Light"}
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400">
              Switch between light and dark theme
            </div>
          </div>
          <div className="ml-4 flex-shrink-0 max-[1000px]:ml-0">
            <DarkLightMode />
          </div>
        </div>
      </div>

      {/* Display Settings */}
      <div className="space-y-1">
        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="h-4 w-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25A2.25 2.25 0 015.25 3h13.5A2.25 2.25 0 0121 5.25z"
            />
          </svg>
          Display
        </h3>

        <div className="dark:hover:bg-gray-750 ml-8 flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 hover:bg-gray-100 max-[1000px]:flex-col max-[1000px]:items-start dark:border-gray-700 dark:bg-gray-800">
          <div className="flex flex-col">
            <div className="text-base font-medium">Font Size</div>
            <div className="text-xs text-gray-600 dark:text-gray-400">
              Adjust the font size across all tables in the application
            </div>
          </div>
          <div className="ml-4 flex-shrink-0 max-[1000px]:ml-0">
            <SizeSelector
              value={fontSize}
              onChange={setFontSize}
              options={fontSizeOptions}
            />
          </div>
        </div>

        <div className="dark:hover:bg-gray-750 ml-8 flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 hover:bg-gray-100 max-[1000px]:flex-col max-[1000px]:items-start dark:border-gray-700 dark:bg-gray-800">
          <div className="flex flex-col">
            <div className="text-base font-medium">Table Row Height</div>
            <div className="text-xs text-gray-600 dark:text-gray-400">
              Adjust the height of rows in all tables throughout the application
            </div>
          </div>
          <div className="ml-4 flex-shrink-0 max-[1000px]:ml-0">
            <SizeSelector
              value={rowHeight}
              onChange={setRowHeight}
              options={rowHeightOptions}
            />
          </div>
        </div>
      </div>

      {/* About Section */}
      <div className="space-y-1">
        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="h-4 w-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z"
            />
          </svg>
          About
        </h3>

        <div className="ml-8 rounded-lg border border-gray-200  bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
          <div className="flex flex-col space-y-2 text-sm">
            <div className="flex flex-row">
              <div className="w-28 font-medium text-gray-700 dark:text-gray-300">
                Version:
              </div>
              <div className="text-gray-600 dark:text-gray-400">
                {getConfig().version}
              </div>
            </div>
            <div className="flex flex-row">
              <div className="w-28 font-medium text-gray-700 dark:text-gray-300">
                API Version:
              </div>
              <div className="text-gray-600 dark:text-gray-400">
                {apiVersion}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Release Notes Section */}
      {releaseNote && (
        <div
          ref={releaseNotesRef}
          className="space-y-1"
          data-testid="GeneralIndexReleaseNotesContainer"
          id="GeneralIndexReleaseNotesContainer"
        >
          <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="h-4 w-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 01-2.25 2.25M16.5 7.5V18a2.25 2.25 0 002.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 002.25 2.25h13.5M6 7.5h3v3H6v-3z"
              />
            </svg>
            Release Notes
            <span className="text-xs font-normal normal-case text-gray-400">
              — {returnStringFormattedDateTime(releaseNote.date)}
            </span>
          </h3>

          <div
            className="settings-scroll ml-8 rounded-lg border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800"
            style={{ height: "300px", overflowY: "auto" }}
          >
            <div
              className="ql-editor"
              dangerouslySetInnerHTML={{
                __html: sanitizeReleaseNoteHtml(releaseNote.content),
              }}
            />
          </div>
        </div>
      )}

      {/* Info Section */}
      <div className="space-y-1">
        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="h-4 w-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
            />
          </svg>
          Info
        </h3>
        <div className="ml-8">
          <InfoBanner title="Settings Storage">
            Your settings are stored locally in your browser and are specific to
            this device. If you clear your browser data or use a different
            device, you&apos;ll need to reconfigure your preferences.
          </InfoBanner>
        </div>
      </div>
    </div>
  );
};

export default GeneralIndex;
